import { create } from 'zustand';
import { disconnectSocket } from '@/lib/socket';
import { clearImpersonation } from '@/lib/impersonation';
import { publicApi } from '@/lib/public-api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  roleId: string;
  permissions: string[];
  /** Licensable modules enabled for this user's tenant (undefined until /auth/me resolves). */
  enabledModules?: string[];
  /** Set when this is a partner's login. The whole portal narrows to that partner. */
  partnerId?: string | null;
  partnerName?: string | null;
  partnerCode?: string | null;
  /** Present during Login as Partner: who is acting, and until when. */
  impersonation?: { sessionId: string; staffName: string | null; reason: string; expiresAt: string } | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  login: (user: User, token: string, refreshToken: string) => void;
  logout: () => void;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
}

/**
 * Tell the API a refresh token is finished with, so it cannot open another session for the rest of its
 * week. Best-effort and never awaited: signing out must not wait on the network or fail because of it.
 *
 * - publicApi, not the main client, which answers a 401 by renewing the session — the opposite of what
 *   is happening here.
 * - The refresh token alone is enough, so an access token that expired meanwhile cannot stop it; the
 *   access token goes along when there is one.
 * - keepalive: the page may be gone a moment later (a closed tab, or a renewal that ends in a full
 *   navigation to /login), and that aborts a plain request still on the wire.
 */
export function revokeRefreshToken(refreshToken: string, accessToken?: string | null): void {
  publicApi
    .post(
      '/auth/logout',
      { refreshToken },
      {
        timeout: 15_000,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        adapter: 'fetch',
        fetchOptions: { keepalive: true },
      },
    )
    .catch(() => {
      // Offline, throttled or already revoked: the sign-out here has happened either way.
    });
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('isp_token'),
  refreshToken: localStorage.getItem('isp_refresh_token'),
  isAuthenticated: !!localStorage.getItem('isp_token'),

  login: (user, token, refreshToken) => {
    localStorage.setItem('isp_token', token);
    localStorage.setItem('isp_refresh_token', refreshToken);
    set({ user, token, refreshToken, isAuthenticated: true });
  },

  logout: () => {
    // During Login as Partner, signing out (or the idle timer) means leaving the partner session.
    // The staff member's own session — and every other tab using it — must survive, so nothing is revoked.
    const impersonation = clearImpersonation();
    if (impersonation) {
      window.location.assign(impersonation.returnTo || '/partners');
      return;
    }
    // Read from storage, not the store: another tab may have renewed the pair since this one last looked.
    const token = localStorage.getItem('isp_token');
    const refreshToken = localStorage.getItem('isp_refresh_token');
    disconnectSocket();
    localStorage.removeItem('isp_token');
    localStorage.removeItem('isp_refresh_token');
    set({ user: null, token: null, refreshToken: null, isAuthenticated: false });
    if (refreshToken) revokeRefreshToken(refreshToken, token);
  },

  setUser: (user) => {
    set({ user });
  },

  setToken: (token) => {
    localStorage.setItem('isp_token', token);
    set({ token });
  },
}));
